/**
 * Free Gift with Purchase Handler
 * Automatically adds a free gift when a trigger product is added,
 * limits the free gift to 1 per order, auto-removes the gift if triggers
 * are removed, applies the configured discount code, and allows customers
 * to remove the free gift if they choose not to receive it.
 */
class FreeGiftHandler {
  constructor() {
    this.config = window.freeGiftConfig || {};
    this.isSyncing = false;
    this.storageKey = 'srg_free_gift_declined';
    this.previousHadGift = false;
    this.init();
  }

  init() {
    if (!this.config.enabled || !this.config.giftVariantId) return;

    // Check cart state on load
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.checkCart());
    } else {
      this.checkCart();
    }

    // Attach listeners for cart forms, checkout & free gift removal
    this.setupCheckoutInterceptor();
    this.setupRemoveInterceptor();
  }

  isGiftDeclined() {
    try {
      return sessionStorage.getItem(this.storageKey) === 'true';
    } catch (e) {
      return Boolean(this._declinedFallback);
    }
  }

  setGiftDeclined(declined) {
    try {
      if (declined) {
        sessionStorage.setItem(this.storageKey, 'true');
        this._declinedFallback = true;
      } else {
        sessionStorage.removeItem(this.storageKey);
        this._declinedFallback = false;
      }
    } catch (e) {
      this._declinedFallback = Boolean(declined);
    }
  }

  setupRemoveInterceptor() {
    // Intercept clicks on free gift remove buttons before cart change fires
    document.addEventListener(
      'click',
      (event) => {
        const removeBtn = event.target.closest('cart-remove-button');
        if (!removeBtn) return;

        const isFreeGift =
          removeBtn.dataset.isFreeGift === 'true' ||
          Boolean(removeBtn.closest('.drawer__cart-item--free-gift, .cart-item--free-gift'));

        if (isFreeGift) {
          this.setGiftDeclined(true);
          this.clearDiscountInputs();
        }
      },
      true
    );
  }

  getTriggerProductIds() {
    return Array.isArray(this.config.triggerProductIds)
      ? this.config.triggerProductIds.map(Number)
      : [];
  }

  isTriggerProduct(productId) {
    if (!productId) return false;
    const pId = Number(productId);
    return this.getTriggerProductIds().includes(pId);
  }

  hasTriggerInCart(cart) {
    if (!cart || !Array.isArray(cart.items)) return false;
    const triggerIds = this.getTriggerProductIds();
    return cart.items.some((item) => triggerIds.includes(Number(item.product_id)));
  }

  getGiftItem(cart) {
    if (!cart || !Array.isArray(cart.items)) return null;
    const giftVariantId = Number(this.config.giftVariantId);
    const giftProductId = Number(this.config.giftProductId);

    // First check for item explicitly tagged with _free_gift property
    let item = cart.items.find(
      (item) =>
        item.properties &&
        (item.properties._free_gift === 'true' || item.properties['_free_gift'] === 'true')
    );
    if (item) return item;

    // Next match by variant ID or product ID
    return (
      cart.items.find(
        (item) =>
          Number(item.variant_id) === giftVariantId ||
          Number(item.id) === giftVariantId ||
          (giftProductId && Number(item.product_id) === giftProductId)
      ) || null
    );
  }

  applyDiscount() {
    const code = this.config.discountCode;
    if (!code) return Promise.resolve();

    // Ensure hidden discount inputs exist in cart forms
    this.syncDiscountInputs(code);

    // Call Shopify discount session endpoint
    return fetch(`/discount/${encodeURIComponent(code)}`).catch((err) =>
      console.warn('Free Gift: Error applying discount session:', err)
    );
  }

  syncDiscountInputs(code) {
    if (!code) return;
    const forms = document.querySelectorAll('form[action*="/cart"], form#CartDrawer-Form, form#cart');
    forms.forEach((form) => {
      let input = form.querySelector('input[name="discount"]');
      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'discount';
        form.appendChild(input);
      }
      input.value = code;
    });
  }

  clearDiscountInputs() {
    const forms = document.querySelectorAll('form[action*="/cart"], form#CartDrawer-Form, form#cart');
    forms.forEach((form) => {
      const input = form.querySelector('input[name="discount"]');
      if (input && (!this.config.discountCode || input.value === this.config.discountCode)) {
        input.remove();
      }
    });
  }

  setupCheckoutInterceptor() {
    document.addEventListener('click', (event) => {
      const checkoutBtn = event.target.closest('[name="checkout"], #CartDrawer-Checkout, #checkout');
      if (!checkoutBtn) return;

      const code = this.config.discountCode;
      if (!code) return;

      const hasGiftInDom = Boolean(
        document.querySelector('.drawer__cart-item--free-gift, .cart-item--free-gift')
      );

      if (hasGiftInDom && !this.isGiftDeclined()) {
        this.syncDiscountInputs(code);
      } else {
        this.clearDiscountInputs();
      }
    });
  }

  getCartDrawerSections() {
    const drawer = document.querySelector('cart-drawer');
    if (drawer && typeof drawer.getSectionsToRender === 'function') {
      return drawer.getSectionsToRender().map((s) => s.id);
    }
    return ['cart-drawer', 'cart-icon-bubble', 'cart-icon-bubble--mobile'];
  }

  checkCart() {
    if (this.isSyncing || !this.config.enabled) return;
    fetch('/cart.js')
      .then((res) => res.json())
      .then((cart) => this.syncCart(cart))
      .catch((err) => console.warn('Free Gift: Error checking cart:', err));
  }

  async handleTriggerProductAdded(response, cartElement) {
    if (!this.config.enabled || !this.config.giftVariantId) return response;

    // If the customer has explicitly removed/declined the gift, do not re-add
    if (this.isGiftDeclined()) return response;

    try {
      const cartRes = await fetch('/cart.js');
      const cart = await cartRes.json();
      const giftItem = this.getGiftItem(cart);

      // If free gift not yet present in cart, add it and fetch updated sections
      if (!giftItem && this.config.giftAvailable) {
        this.isSyncing = true;
        await this.applyDiscount();

        const sections = cartElement && typeof cartElement.getSectionsToRender === 'function'
          ? cartElement.getSectionsToRender().map((s) => s.id)
          : this.getCartDrawerSections();

        const addGiftRes = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: this.config.giftVariantId,
                quantity: 1,
                properties: { _free_gift: 'true' },
              },
            ],
            sections: sections,
            sections_url: window.location.pathname,
          }),
        });

        const giftData = await addGiftRes.json();
        this.previousHadGift = true;
        this.isSyncing = false;
        return giftData;
      }
    } catch (e) {
      console.warn('Free Gift: Error handling trigger product addition:', e);
      this.isSyncing = false;
    }

    return response;
  }

  async onCartChanged(parsedState, cartItemsInstance) {
    if (this.isSyncing || !this.config.enabled || !parsedState || !parsedState.items) return;

    const hasTrigger = this.hasTriggerInCart(parsedState);
    const giftItem = this.getGiftItem(parsedState);

    // Case 1: Trigger products removed -> remove the free gift and reset declined flag
    if (!hasTrigger) {
      this.setGiftDeclined(false);
      this.clearDiscountInputs();
      this.previousHadGift = false;

      if (giftItem) {
        this.isSyncing = true;
        try {
          const sections = cartItemsInstance && typeof cartItemsInstance.getSectionsToRender === 'function'
            ? cartItemsInstance.getSectionsToRender().map((s) => s.section)
            : ['cart-drawer', 'main-cart-items'];

          const response = await fetch(`${window.routes?.cart_change_url || '/cart/change.js'}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: giftItem.key,
              quantity: 0,
              sections: sections,
              sections_url: window.location.pathname,
            }),
          });

          const newState = await response.json();
          this.renderUpdatedSections(newState, cartItemsInstance);
        } catch (err) {
          console.warn('Free Gift: Error removing free gift:', err);
        } finally {
          this.isSyncing = false;
        }
      }
      return;
    }

    // Case 2: Gift was removed by customer while triggers remain
    if (hasTrigger && !giftItem) {
      this.setGiftDeclined(true);
      this.clearDiscountInputs();
      this.previousHadGift = false;
      return;
    }

    this.previousHadGift = true;

    // Case 3: Quantity of gift exceeds 1 -> lock back to 1
    if (hasTrigger && giftItem && giftItem.quantity > 1) {
      this.isSyncing = true;
      try {
        const sections = cartItemsInstance && typeof cartItemsInstance.getSectionsToRender === 'function'
          ? cartItemsInstance.getSectionsToRender().map((s) => s.section)
          : ['cart-drawer', 'main-cart-items'];

        const response = await fetch(`${window.routes?.cart_change_url || '/cart/change.js'}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            id: giftItem.key,
            quantity: 1,
            sections: sections,
            sections_url: window.location.pathname,
          }),
        });

        const newState = await response.json();
        this.renderUpdatedSections(newState, cartItemsInstance);
        await this.applyDiscount();
      } catch (err) {
        console.warn('Free Gift: Error normalizing free gift quantity:', err);
      } finally {
        this.isSyncing = false;
      }
      return;
    }

    // Ensure discount code is applied whenever gift and trigger are present
    if (hasTrigger && giftItem) {
      this.applyDiscount();
    }
  }

  async syncCart(cart) {
    if (this.isSyncing || !this.config.enabled || !this.config.giftVariantId) return;

    const hasTrigger = this.hasTriggerInCart(cart);
    const giftItem = this.getGiftItem(cart);

    if (!hasTrigger) {
      this.setGiftDeclined(false);
      this.clearDiscountInputs();
      this.previousHadGift = false;

      if (giftItem) {
        this.isSyncing = true;
        try {
          const response = await fetch(`${window.routes?.cart_change_url || '/cart/change.js'}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: giftItem.key,
              quantity: 0,
              sections: this.getCartDrawerSections(),
              sections_url: window.location.pathname,
            }),
          });
          const data = await response.json();
          this.renderUpdatedSections(data);
        } catch (err) {
          console.warn('Free Gift: Error syncing gift out of cart:', err);
        } finally {
          this.isSyncing = false;
        }
      }
      return;
    }

    // If customer has opted to remove the gift, respect their choice
    if (this.isGiftDeclined()) {
      this.clearDiscountInputs();
      this.previousHadGift = Boolean(giftItem);
      return;
    }

    if (hasTrigger && !giftItem && this.config.giftAvailable) {
      this.isSyncing = true;
      try {
        await this.applyDiscount();
        const response = await fetch(`${window.routes?.cart_add_url || '/cart/add.js'}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: JSON.stringify({
            items: [
              {
                id: this.config.giftVariantId,
                quantity: 1,
                properties: { _free_gift: 'true' },
              },
            ],
            sections: this.getCartDrawerSections(),
            sections_url: window.location.pathname,
          }),
        });
        const data = await response.json();
        this.previousHadGift = true;
        this.renderUpdatedSections(data);
      } catch (err) {
        console.warn('Free Gift: Error syncing gift into cart:', err);
      } finally {
        this.isSyncing = false;
      }
      return;
    }

    if (hasTrigger && giftItem) {
      this.previousHadGift = true;
      this.applyDiscount();
    }
  }

  renderUpdatedSections(parsedState, cartItemsInstance) {
    if (!parsedState || !parsedState.sections) return;

    const cartDrawer = document.querySelector('cart-drawer');
    if (cartDrawer && typeof cartDrawer.renderContents === 'function') {
      cartDrawer.renderContents(parsedState);
      return;
    }

    if (cartItemsInstance && typeof cartItemsInstance.getSectionsToRender === 'function') {
      cartItemsInstance.getSectionsToRender().forEach((section) => {
        const target =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);
        if (target && parsedState.sections[section.section]) {
          const html = new DOMParser()
            .parseFromString(parsedState.sections[section.section], 'text/html')
            .querySelector(section.selector)?.innerHTML;
          if (html) target.innerHTML = html;
        }
      });
    }
  }
}

window.FreeGift = new FreeGiftHandler();
